import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';
import { monthRange, fmtDate } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const PRIORITY_COLOR = { Blocker: '#dc2626', High: '#ea580c', Medium: '#2563eb', Minor: '#16a34a', Low: '#6b7280' };
const priColor = (p) => PRIORITY_COLOR[p] || '#94a3b8';

function fmtH(h) {
  if (!h && h !== 0) return '—';
  if (h >= 48) return `${Math.round(h / 24)}j`;
  if (h >= 24) return `${(h / 24).toFixed(1)}j`;
  return `${Math.round(h)}h`;
}

function fmtSeconds(s) {
  if (!s) return '—';
  const h = s / 3600;
  if (h >= 8) return `${(h / 8).toFixed(1)}j`;
  return `${Math.round(h)}h`;
}

function PriBadge({ p }) {
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: priColor(p) + '20', color: priColor(p), fontWeight: 700, fontSize: 11 }}>{p || '—'}</span>;
}

export default function ClientReport() {
  const [clients, setClients] = useState([]);
  const [project, setProject] = useState('');
  const [offset, setOffset] = useState(0);
  const [fromDate, setFromDate] = useState(monthRange(0).from);
  const [toDate, setToDate] = useState(monthRange(0).to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jiraUrl, setJiraUrl] = useState(localStorage.getItem('jiraUrl') || '');
  const [tab, setTab] = useState('resolved');

  useEffect(() => {
    fetch(`${API}/api/reports/client-list`).then(r => r.json()).then(d => {
      setClients(d);
      if (d.length > 0 && !project) setProject(d[0].key);
    }).catch(() => {});
    fetch(`${API}/api/settings`).then(r => r.json()).then(d => {
      if (d.jira_url) { setJiraUrl(d.jira_url); localStorage.setItem('jiraUrl', d.jira_url); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const r = monthRange(offset);
    setFromDate(r.from); setToDate(r.to);
  }, [offset]);

  const load = () => {
    if (!project) return;
    setLoading(true);
    fetch(`${API}/api/reports/client?project=${encodeURIComponent(project)}&from_date=${fromDate}&to_date=${toDate}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (project) load(); }, [project, fromDate, toDate]);

  const TicketKey = ({ k }) => jiraUrl
    ? <a href={`${jiraUrl}/browse/${k}`} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', fontSize: 12 }}>{k}</a>
    : <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{k}</span>;

  const list = tab === 'resolved' ? (data?.resolved || []) : (data?.open || []);

  return (
    <div>
      <ReportNote what="Activity and logged time for the selected client." from={fromDate} to={toDate} />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <select value={project} onChange={e => setProject(e.target.value)} className="input" style={{ minWidth: 200, fontSize: 14 }}>
          {clients.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setOffset(o => o - 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 14, minWidth: 140, textAlign: 'center' }}>{monthRange(offset).label}</span>
          <button onClick={() => setOffset(o => o + 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, borderLeft: '1px solid var(--border)' }}>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input" style={{ width: 130 }} />
          <span style={{ color: 'var(--text-2)' }}>→</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input" style={{ width: 130 }} />
          <button onClick={load} className="btn btn-primary" style={{ padding: '6px 14px' }}>Apply</button>
        </div>
        <button onClick={() => window.print()} className="btn btn-secondary" style={{ marginLeft: 'auto' }}>Print / PDF</button>
      </div>

      {loading && <div className="loading">Loading client report…</div>}

      {!loading && data && (
        <div className="print-area">
          {/* Print header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
              Client Report — {data.project}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              Period: {fmtDate(data.from_date)} → {fmtDate(data.to_date)} · Generated on {fmtDate(new Date().toISOString())}
            </div>
          </div>

          {/* KPI row */}
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
              <div className="card-title">Resolved tickets</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{data.total_resolved}</div>
              <div className="card-sub">in the period</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--teal)' }}>
              <div className="card-title">Open tickets</div>
              <div className="card-value" style={{ color: data.total_open > 5 ? 'var(--critical)' : 'var(--teal)' }}>{data.total_open}</div>
              <div className="card-sub">currently open</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--ok)' }}>
              <div className="card-title">Avg. resolution time</div>
              <div className="card-value" style={{ fontSize: 22 }}>{fmtH(data.avg_hours_to_resolve)}</div>
              <div className="card-sub">to resolve</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--warn)' }}>
              <div className="card-title">By priority</div>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.by_priority.map(p => (
                  <div key={p.priority} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: priColor(p.priority), flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--text-2)', flex: 1 }}>{p.priority}</span>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{p.count}</span>
                  </div>
                ))}
                {data.by_priority.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>—</span>}
              </div>
            </div>
          </div>

          {/* Ticket table (screen only with tabs) */}
          <div className="card no-print" style={{ marginBottom: 16 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
              {[['resolved', `Resolved (${data.total_resolved})`], ['open', `Open (${data.total_open})`]].map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} style={{ padding: '4px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: tab === key ? 'var(--accent)' : 'var(--bg-2)', color: tab === key ? '#fff' : 'var(--text-2)' }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Ticket', 'Summary', 'Priority', 'Assignee', 'Status', tab === 'resolved' ? 'Resolved on' : 'Created on', tab === 'resolved' ? 'Duration' : 'Age', 'Logged'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map(t => (
                    <tr key={t.issue_key} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}><TicketKey k={t.issue_key} /></td>
                      <td style={{ padding: '9px 14px', fontSize: 12, color: 'var(--text-2)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.summary}>{t.summary}</td>
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}><PriBadge p={t.priority} /></td>
                      <td style={{ padding: '9px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>{(t.assignee || '').split(' ')[0]}</td>
                      <td style={{ padding: '9px 14px', fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{t.status}</td>
                      <td style={{ padding: '9px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>{tab === 'resolved' ? fmtDate(t.resolved_date) : fmtDate(t.created_date)}</td>
                      <td style={{ padding: '9px 14px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{tab === 'resolved' ? fmtH(t.hours_open) : `${t.age_days}j`}</td>
                      <td style={{ padding: '9px 14px', fontSize: 12, whiteSpace: 'nowrap', color: t.logged_seconds ? 'var(--teal)' : 'var(--text-3)' }}>{fmtSeconds(t.logged_seconds)}</td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 28, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>No tickets</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Print-only: both tables */}
          <div className="print-only" style={{ display: 'none' }}>
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 13 }}>Resolved tickets ({data.total_resolved})</div>
            {data.resolved.length === 0 ? <p style={{ color: '#888', fontSize: 12 }}>No resolved tickets in this period.</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 24 }}>
                <thead><tr>{['Ticket','Summary','Priority','Assignee','Resolved on','Duration','Logged'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #ccc', fontWeight: 700, color: '#555', fontSize: 9, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
                <tbody>{data.resolved.map(t => <tr key={t.issue_key} style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '4px 8px', fontFamily: 'monospace', fontWeight: 700, fontSize: 10 }}>{t.issue_key}</td><td style={{ padding: '4px 8px', maxWidth: 260 }}>{t.summary}</td><td style={{ padding: '4px 8px' }}>{t.priority}</td><td style={{ padding: '4px 8px' }}>{(t.assignee || '').split(' ')[0]}</td><td style={{ padding: '4px 8px' }}>{fmtDate(t.resolved_date)}</td><td style={{ padding: '4px 8px' }}>{fmtH(t.hours_open)}</td><td style={{ padding: '4px 8px' }}>{fmtSeconds(t.logged_seconds)}</td></tr>)}</tbody>
              </table>
            )}
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 13 }}>Open tickets ({data.total_open})</div>
            {data.open.length === 0 ? <p style={{ color: '#888', fontSize: 12 }}>No open tickets.</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr>{['Ticket','Summary','Priority','Assignee','Status','Age','Logged'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #ccc', fontWeight: 700, color: '#555', fontSize: 9, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
                <tbody>{data.open.map(t => <tr key={t.issue_key} style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '4px 8px', fontFamily: 'monospace', fontWeight: 700, fontSize: 10 }}>{t.issue_key}</td><td style={{ padding: '4px 8px', maxWidth: 260 }}>{t.summary}</td><td style={{ padding: '4px 8px' }}>{t.priority}</td><td style={{ padding: '4px 8px' }}>{(t.assignee || '').split(' ')[0]}</td><td style={{ padding: '4px 8px' }}>{t.status}</td><td style={{ padding: '4px 8px' }}>{t.age_days}j</td><td style={{ padding: '4px 8px' }}>{fmtSeconds(t.logged_seconds)}</td></tr>)}</tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .sidebar, .topbar, .topnav { display: none !important; }
          .main { margin: 0 !important; padding: 16px !important; }
          .grid-4 { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}
